import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit, Trash2, Save, X, FileText, Eye, EyeOff, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadPosts = useCallback(async () => {
    try {
      const response = await adminApi.get('/api/admin/blog/posts');
      setPosts(response.data);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les articles", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await adminApi.delete(`/api/admin/blog/posts/${postId}`);
      toast({ title: "Article supprimé" });
      loadPosts();
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de supprimer", variant: "destructive" });
    }
  };

  const handleSave = async (postData) => {
    try {
      if (postData.id && editingPost) {
        await adminApi.put(`/api/admin/blog/posts/${postData.id}`, postData);
        toast({ title: "Article mis à jour !" });
      } else {
        await adminApi.post('/api/admin/blog/posts', postData);
        toast({ title: "Article créé !" });
      }
      setEditingPost(null);
      setShowForm(false);
      loadPosts();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de sauvegarder", variant: "destructive" });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-400 text-lg">Chargement...</p></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="blog-manager-title">Gestion du Blog</h1>
          <p className="text-gray-400">{posts.length} article(s)</p>
        </div>
        <Button onClick={() => { setEditingPost(null); setShowForm(true); }} className="bg-orange-600 hover:bg-orange-700" data-testid="new-post-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel Article
        </Button>
      </div>

      {showForm && (
        <BlogPostForm
          post={editingPost}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingPost(null); }}
        />
      )}

      <div className="grid grid-cols-1 gap-4">
        {posts.map((post) => (
          <div key={post.id} className="bg-gray-900 rounded-lg p-6 border border-gray-800 hover:border-gray-700 transition-colors" data-testid={`blog-item-${post.id}`}>
            <div className="flex items-start gap-4">
              {post.image && (
                <img src={post.image} alt={post.title} className="w-24 h-16 object-cover rounded-lg" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold text-lg">{post.title}</h3>
                  {!post.published && (
                    <span className="bg-yellow-600/20 text-yellow-400 text-xs px-2 py-0.5 rounded">Brouillon</span>
                  )}
                </div>
                <p className="text-gray-400 text-sm mb-2 line-clamp-1">{post.excerpt}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">Catégorie: <span className="text-purple-400">{post.category}</span></span>
                  <span className="text-gray-500">{new Date(post.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  onClick={() => { setEditingPost(post); setShowForm(true); }}
                  className="border-gray-700 text-white hover:bg-gray-800">
                  <Edit className="w-4 h-4 mr-1" /> Modifier
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => handleDelete(post.id)}
                  className="border-red-700 text-red-500 hover:bg-red-900">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-20 bg-gray-900 rounded-lg border border-gray-800">
          <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-4">Aucun article</p>
          <Button onClick={() => setShowForm(true)} className="bg-orange-600 hover:bg-orange-700">
            Créer votre premier article
          </Button>
        </div>
      )}
    </div>
  );
};


const BlogPostForm = ({ post, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    image: post?.image || '',
    category: post?.category || 'general',
    tags: post?.tags || [],
    metaTitle: post?.metaTitle || '',
    metaDescription: post?.metaDescription || '',
    published: post?.published !== undefined ? post.published : true,
    sortOrder: post?.sortOrder || 0,
    id: post?.id || undefined,
  });
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug || !formData.content) {
      toast({ title: "Erreur", description: "Titre, slug et contenu sont obligatoires", variant: "destructive" });
      return;
    }
    onSave(formData);
  };

  const generateSlug = () => {
    const slug = formData.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setFormData({ ...formData, slug });
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await adminApi.post('/api/admin/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image: `${BACKEND_URL}${response.data.url}` }));
      toast({ title: "Image uploadée" });
    } catch (error) {
      toast({ title: "Erreur d'upload", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-gray-900 rounded-lg p-8 border border-gray-800 mb-8">
      <h2 className="text-2xl font-bold text-white mb-6">
        {post ? 'Modifier l\'article' : 'Nouvel article'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Title + Slug */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Titre *</label>
            <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="bg-gray-800 text-white border-gray-700" placeholder="Titre de l'article" data-testid="blog-title-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Slug (URL) *</label>
            <div className="flex gap-2">
              <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required className="bg-gray-800 text-white border-gray-700" placeholder="titre-de-larticle" data-testid="blog-slug-input" />
              <Button type="button" onClick={generateSlug} variant="outline" className="border-gray-700 text-white">Auto</Button>
            </div>
          </div>
        </div>

        {/* Category + Published + Sort */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Catégorie</label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger className="bg-gray-800 text-white border-gray-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">Général</SelectItem>
                <SelectItem value="mariage">Mariage</SelectItem>
                <SelectItem value="festival">Festival</SelectItem>
                <SelectItem value="corporate">Corporate</SelectItem>
                <SelectItem value="soiree">Soirée</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Statut</label>
            <Select value={formData.published ? 'true' : 'false'} onValueChange={(v) => setFormData({ ...formData, published: v === 'true' })}>
              <SelectTrigger className="bg-gray-800 text-white border-gray-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Publié</SelectItem>
                <SelectItem value="false">Brouillon</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Ordre</label>
            <Input type="number" value={formData.sortOrder} onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} className="bg-gray-800 text-white border-gray-700" />
          </div>
        </div>

        {/* Excerpt */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Extrait</label>
          <Textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} rows={2} className="bg-gray-800 text-white border-gray-700" placeholder="Résumé court de l'article..." data-testid="blog-excerpt-input" />
        </div>

        {/* Content */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Contenu (HTML) *</label>
          <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={12} className="bg-gray-800 text-white border-gray-700 font-mono text-sm" placeholder="<h2>Titre</h2><p>Contenu...</p>" data-testid="blog-content-input" />
        </div>

        {/* Image */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Image de couverture</label>
          {formData.image && (
            <div className="mb-3 relative inline-block">
              <img src={formData.image} alt="" className="w-48 h-28 object-cover rounded-lg" />
              <button type="button" onClick={() => setFormData({ ...formData, image: '' })} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-purple-600 hover:bg-purple-700" data-testid="blog-upload-image-btn">
              <Upload className="w-4 h-4 mr-2" />{uploading ? 'Upload...' : 'Uploader'}
            </Button>
            <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} className="bg-gray-800 text-white border-gray-700" placeholder="Ou collez une URL..." />
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.tags.map((tag, i) => (
              <span key={tag} className="flex items-center gap-1 bg-gray-800 text-gray-300 px-3 py-1 rounded-lg text-sm">
                {tag}
                <button type="button" onClick={() => removeTag(i)} className="text-red-400 hover:text-red-300 ml-1"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} className="bg-gray-800 text-white border-gray-700" placeholder="Nouveau tag..." />
            <Button type="button" onClick={addTag} variant="outline" className="border-gray-700 text-white"><Plus className="w-4 h-4 mr-1" />Tag</Button>
          </div>
        </div>

        {/* SEO */}
        <div className="border-t border-gray-800 pt-5">
          <h3 className="text-white font-semibold mb-4">SEO</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Meta Title</label>
              <Input value={formData.metaTitle} onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })} className="bg-gray-800 text-white border-gray-700" placeholder="Titre pour Google (60 car. max)" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Meta Description</label>
              <Textarea value={formData.metaDescription} onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })} rows={2} className="bg-gray-800 text-white border-gray-700" placeholder="Description pour Google (155 car. max)" />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 py-3" data-testid="blog-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {post ? 'Mettre à jour' : 'Publier l\'article'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="border-gray-700 text-white hover:bg-gray-800">
            <X className="w-4 h-4 mr-2" />Annuler
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BlogManager;
